"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.HeadTag = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const head_1 = __importDefault(require("next/head"));
const router_1 = require("next/router");
const jsonld_1 = require("./jsonld");
const utils_1 = require("../../utils");
// NOTE: <title> and <meta> elements need to be contained as direct children of the <Head> element,
// or wrapped into maximum one level of <React.Fragment>, otherwise
// the metatags won't be correctly picked up on clientside navigation.
// NOTE:- Segun parece tampoco acepta `Fragments` que son hermanos. Solo pilla el primero.
const generateJson = {
    'contents': (data) => (0, jsonld_1.article)(data),
    'faqs': (data) => (0, jsonld_1.faqs)(data),
    'home': (data) => (0, jsonld_1.home)(data),
    'product': (data, region, locale) => (0, jsonld_1.product)({ data, region, locale }),
    'products': (data, region, locale) => (0, jsonld_1.products)({ data, region, locale }),
    'search': (_data, _region, locale) => (0, jsonld_1.search)({ locale }),
    'article': (data, region, locale) => (0, jsonld_1.article)({ data, region, locale, hostname: utils_1.HOSTNAME }),
    'journal': (data, region, locale) => (0, jsonld_1.articles)({ data, region, locale, hostname: utils_1.HOSTNAME })
};
const defaultOgImage = {
    default: `https://${utils_1.HOSTNAME}/images/og_cover.jpg`,
    content_type: 'image/jpg',
    alt: 'Mammafiore',
    size: {
        height: 627,
        width: 1200
    }
};
const pathnameByPageType = {
    'home': '/',
    'contents': '/page',
    'products': '/catalog',
    'product': '/catalog/product',
};
const generateCanonicalAndAlternates = ({ locale, pageType, slug }) => {
    var _a;
    const localeSlug = (slug !== 'search') ? (typeof slug === 'string') ? slug : slug[locale] : '';
    const href = `https://${utils_1.HOSTNAME}${(_a = pathnameByPageType[pageType]) !== null && _a !== void 0 ? _a : `/${pageType}`}${localeSlug ? `/${localeSlug}` : ''}`;
    const hrefLang = locale;
    return (0, jsx_runtime_1.jsx)("link", { rel: 'canonical', href: href, hrefLang: hrefLang }, 'canonical');
};
const robots = utils_1.PRO && (utils_1.NEXT_PUBLIC_LOCALE === 'es-ES' || utils_1.NEXT_PUBLIC_LOCALE === 'fr-FR' || utils_1.NEXT_PUBLIC_LOCALE === 'en-GB' || utils_1.NEXT_PUBLIC_LOCALE === 'de-DE' || utils_1.NEXT_PUBLIC_LOCALE === 'pt-PT') ? 'all' : 'noindex, follow';
const HeadTag = ({ seo, jsonld = null, slug = null, pageType = null }) => {
    var _a;
    const { locale: localeRouter, query } = (0, router_1.useRouter)();
    const locale = (0, utils_1.getLang)(localeRouter);
    const image = defaultOgImage;
    const OgImages = [
        (0, jsx_runtime_1.jsx)("meta", { property: 'og:image', content: image.default }, 'og:image'),
        (0, jsx_runtime_1.jsx)("meta", { property: 'og:image:alt', content: image.alt }, 'og:image:alt'),
        (0, jsx_runtime_1.jsx)("meta", { property: 'og:image:type', content: image.content_type }, 'og:image:type'),
        (0, jsx_runtime_1.jsx)("meta", { property: 'og:image:secure_url', content: image.default }, 'og:image:secure_url'),
        (0, jsx_runtime_1.jsx)("meta", { property: 'og:image:height', content: `${image.size.height}` }, 'og:image:height'),
        (0, jsx_runtime_1.jsx)("meta", { property: 'og:image:width', content: `${image.size.width}` }, 'og:image:width'),
    ];
    const OgBase = [
        (0, jsx_runtime_1.jsx)("meta", { property: 'og:description', content: seo.description }, 'og:description'),
        (0, jsx_runtime_1.jsx)("meta", { property: 'og:locale', content: `${locale}` }, 'og:locale'),
        (0, jsx_runtime_1.jsx)("meta", { property: 'og:site_name', content: 'Mammafiore' }, 'og:site_name'),
        (0, jsx_runtime_1.jsx)("meta", { property: 'og:title', content: seo.title }, 'og:title'),
        (0, jsx_runtime_1.jsx)("meta", { property: 'og:type', content: 'website' }, 'og:type'),
        (0, jsx_runtime_1.jsx)("meta", { property: 'og:url', content: utils_1.HOSTNAME }, 'og:url'),
    ];
    const OgVideo = seo.video ? [
        (0, jsx_runtime_1.jsx)("meta", { property: 'og:video:type', content: seo.video.content_type }, 'og:video:type'),
        (0, jsx_runtime_1.jsx)("meta", { property: 'og:video:secure_url', content: seo.video.default }, 'og:video:secure_url'),
        (0, jsx_runtime_1.jsx)("meta", { property: 'og:video:height', content: `${seo.video.size.height}` }, 'og:video:height'),
        (0, jsx_runtime_1.jsx)("meta", { property: 'og:video:width', content: `${seo.video.size.width}` }, 'og:video:width'),
    ] : [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const CanonicalAndAlternates = (0, react_1.useMemo)(() => slug || slug === '' ? generateCanonicalAndAlternates({ locale, pageType, slug }) : [], [locale, pageType, slug]);
    return ((0, jsx_runtime_1.jsxs)(head_1.default, { children: [(0, jsx_runtime_1.jsx)("title", { children: seo.title }), (0, jsx_runtime_1.jsx)("meta", { name: 'description', content: seo.description }), (0, jsx_runtime_1.jsx)("meta", { name: 'robots', content: robots }), OgBase, OgImages, OgVideo, jsonld && (0, jsx_runtime_1.jsx)("script", { type: 'application/ld+json', dangerouslySetInnerHTML: { __html: generateJson[(_a = jsonld.type) !== null && _a !== void 0 ? _a : pageType](jsonld.data, query.region, locale) } }), CanonicalAndAlternates] }));
};
exports.HeadTag = HeadTag;
